import React, { useState, useEffect } from 'react';
import db from '../db';
import axiosInstance from '../index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { isDarkMode, toggleDarkMode } from '../utils/theme';
import './Settings.css';

function Settings({ onClose, isOnline }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [showResetAllDialog, setShowResetAllDialog] = useState(false);
  const [showResetDateDialog, setShowResetDateDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    setDarkMode(isDarkMode());
    
    const handleThemeChange = (event) => {
      setDarkMode(event.detail.isDark);
    };
    
    window.addEventListener('themechange', handleThemeChange);
    return () => {
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  const handleThemeToggle = () => {
    const newDarkMode = toggleDarkMode();
    setDarkMode(newDarkMode);
  };

  const handleResetAll = async () => {
    try {
      // Clear local IndexedDB data
      await db.transaction('rw', db.chapterProgress, db.sessions, async () => {
        await db.chapterProgress.clear();
        await db.sessions.clear();
      });
      
      // Clear sync timestamp
      localStorage.removeItem('lastSyncAt');
      
      // If online, also clear backend data
      if (isOnline) {
        try {
          await axiosInstance.delete('/api/reset-all-progress');
        } catch (error) {
          console.error('Error resetting progress on server:', error);
          // Continue even if server request fails - local data is already cleared
        }
      }
      
      setShowResetAllDialog(false);
      setSuccessMessage('All progress tracking has been reset successfully.');
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error resetting progress:', error);
      setSuccessMessage('Failed to reset progress. Please try again.');
      setShowSuccessDialog(true);
    }
  };

  const handleResetBeforeDate = async () => {
    if (!selectedDate) {
      setSuccessMessage('Please select a date.');
      setShowSuccessDialog(true);
      return;
    }

    try {
      const cutoffDate = new Date(selectedDate);
      cutoffDate.setHours(0, 0, 0, 0); // Start of the selected day
      const cutoffTimestamp = cutoffDate.toISOString();

      // Clear local IndexedDB data
      await db.transaction('rw', db.chapterProgress, db.sessions, async () => {
        // Remove chapter progress before the cutoff date
        await db.chapterProgress
          .where('last_updated_at')
          .below(cutoffTimestamp)
          .delete();

        // Remove sessions before the cutoff date
        await db.sessions
          .where('last_listened_at')
          .below(cutoffTimestamp)
          .delete();
      });

      // If online, also clear backend data
      if (isOnline) {
        try {
          await axiosInstance.delete('/api/reset-progress-before-date', {
            data: { cutoff_date: cutoffTimestamp }
          });
        } catch (error) {
          console.error('Error resetting progress on server:', error);
          // Continue even if server request fails - local data is already cleared
        }
      }

      setShowResetDateDialog(false);
      setSelectedDate('');
      setSuccessMessage(`All progress tracking before ${cutoffDate.toLocaleDateString()} has been removed.`);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error resetting progress before date:', error);
      setSuccessMessage('Failed to reset progress. Please try again.');
      setShowSuccessDialog(true);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>Settings</h2>
        <button className="settings-close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h3>Appearance</h3>
          <div className="section-content appearance-section">
            <div className="section-text">
              <p>Toggle between light and dark theme.</p>
            </div>
            <label className="theme-toggle-switch">
              <input 
                type="checkbox" 
                checked={darkMode}
                onChange={handleThemeToggle}
              />
              <span className="slider"></span>
              <span className="switch-icon">
                <FontAwesomeIcon 
                  icon={darkMode ? faMoon : faSun} 
                  style={{ 
                    marginLeft: '8px', 
                    color: 'var(--text-muted)',
                    fontSize: '16px'
                  }} 
                />
              </span>
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>Reset All Progress</h3>
          <div className="section-content">
            <p>Remove all progress tracking data. This will reset all books to "not started" status.</p>
            <button 
              className="reset-button danger full-width"
              onClick={() => setShowResetAllDialog(true)}
            >
              Reset All Progress Tracking
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h3>Reset Progress Before Date</h3>
          <div className="section-content">
            <p>Remove all tracking data from before a specific date. This allows you to start fresh from a particular date while keeping recent progress.</p>
            <div className="date-picker-container">
              <label htmlFor="cutoff-date">Select Date:</label>
              <input
                id="cutoff-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <button 
              className="reset-button danger full-width"
              onClick={() => setShowResetDateDialog(true)}
              disabled={!selectedDate}
            >
              Remove All Tracking Prior to Date
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Reset All */}
      {showResetAllDialog && (
        <div className="confirmation-dialog-overlay" onClick={() => setShowResetAllDialog(false)}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Reset All Progress</h3>
            <p>Are you sure you want to reset all progress tracking? This action cannot be undone.</p>
            <div className="dialog-buttons">
              <button 
                className="cancel-button"
                onClick={() => setShowResetAllDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-button danger"
                onClick={handleResetAll}
              >
                Yes, Reset All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Reset Before Date */}
      {showResetDateDialog && (
        <div className="confirmation-dialog-overlay" onClick={() => setShowResetDateDialog(false)}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Reset Progress Before Date</h3>
            <p>
              Are you sure you want to remove all tracking data from before{' '}
              <strong>{new Date(selectedDate).toLocaleDateString()}</strong>? This action cannot be undone.
            </p>
            <div className="dialog-buttons">
              <button 
                className="cancel-button"
                onClick={() => setShowResetDateDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-button danger"
                onClick={handleResetBeforeDate}
              >
                Yes, Remove Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Info Dialog */}
      {showSuccessDialog && (
        <div className="confirmation-dialog-overlay" onClick={() => setShowSuccessDialog(false)}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Notice</h3>
            <p>{successMessage}</p>
            <div className="dialog-buttons">
              <button 
                className="confirm-button"
                onClick={() => setShowSuccessDialog(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
